import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ArticleCard } from '../components/article-card';
import { ChannelLogo } from '../components/channel-logo';
import { AlertsSection } from '../components/alerts/alerts-section';
import { AlertDetailModal } from '../components/alerts/alert-detail-modal';
import { BookmarkIcon, GlobeIcon, HomeIcon } from '../components/icons';
import { useFeedQuery, useFeedArticles, useSavedQuery } from '../hooks/use-feed-query';
import { useSessionInitQuery } from '../hooks/use-session-init-query';
import { useActiveAlerts } from '../hooks/use-alerts-query';
import { useSubscribedChannelsQuery } from '../hooks/use-channels-query';
import { useProfileQuery } from '../hooks/use-profile-query';
import { useAuth } from '../hooks/use-auth';
import { FALLBACK_CHANNELS, FEED_TOPICS } from '../config/reference-data';
import {
  findActiveLanguage,
  useActivePlatformLanguageOptions,
} from '../hooks/use-platform-languages-query';
import type { FeedAlert } from '../types';

const TOPIC_TO_CATEGORY: Record<string, string | undefined> = {
  'For you': undefined,
  World: 'world',
  Politics: 'politics',
  Housing: 'housing',
  Visa: 'visa',
  Work: 'work',
  Tax: 'tax',
  Tech: 'technology',
  Culture: 'culture',
  Sports: 'sports',
};

export function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [topic, setTopic] = useState<string>('For you');
  const [openAlert, setOpenAlert] = useState<FeedAlert | null>(null);
  // "All ↔ My Channels" toggle. In-memory only — page-load resets to
  // All so the user always lands on their personalized feed.
  const [subscribedOnly, setSubscribedOnly] = useState<boolean>(false);

  // Single bootstrap call — seeds profile / saved / readStatus / unreadCount
  // / subscribedChannelIds caches in one round-trip. The dedicated
  // useProfileQuery / useSavedQuery / useSubscribedChannelsQuery hooks below
  // become cache reads while session-init is hot.
  useSessionInitQuery();
  const profile = useProfileQuery();
  const subscribedChannels = useSubscribedChannelsQuery();
  const alerts = useActiveAlerts();
  const saved = useSavedQuery();

  const category = TOPIC_TO_CATEGORY[topic];
  const feedQuery = useFeedQuery(category, undefined, subscribedOnly);
  const articles = useFeedArticles(category, undefined, subscribedOnly);

  const savedSet = useMemo(() => new Set(saved.data ?? []), [saved.data]);

  const channelsForSidebar = useMemo(() => {
    if (subscribedChannels.data && subscribedChannels.data.length > 0) {
      return subscribedChannels.data.slice(0, 5).map((c) => ({
        id: c.id,
        name: c.name,
        logoUrl: c.logoUrl ?? null,
      }));
    }
    return FALLBACK_CHANNELS.slice(0, 4).map((c) => ({ id: c.id, name: c.name, logoUrl: null }));
  }, [subscribedChannels.data]);

  const greetingName =
    profile.data?.displayName ??
    (user?.user_metadata?.display_name as string | undefined) ??
    user?.email?.split('@')[0] ??
    t('web.home.greeting.fallbackName');
  const activeLanguages = useActivePlatformLanguageOptions();
  const lang = findActiveLanguage(activeLanguages, profile.data?.primaryLanguage);

  const trending = articles.slice(0, 5);

  return (
    <div className="nw-app">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.home.sidebar.feeds')}</div>
        <button
          type="button"
          className="nw-side__item is-active"
          onClick={() => {
            setTopic('For you');
          }}
        >
          <HomeIcon size={16} />
          <span className="nw-side__item-label">{t('web.home.sidebar.home')}</span>
        </button>
        <button type="button" className="nw-side__item" onClick={() => setTopic('World')}>
          <GlobeIcon size={16} />
          <span className="nw-side__item-label">{t('web.home.sidebar.world')}</span>
        </button>
        <button type="button" className="nw-side__item" onClick={() => navigate('/saved')}>
          <BookmarkIcon size={16} />
          <span className="nw-side__item-label">{t('web.home.sidebar.saved')}</span>
        </button>
        <button type="button" className="nw-side__item" onClick={() => navigate('/topics')}>
          <span style={{ fontWeight: 800, fontSize: 15, marginLeft: 2 }}>#</span>
          <span className="nw-side__item-label">{t('web.home.sidebar.topics')}</span>
        </button>
        <div className="nw-side__divider" />
        <div className="nw-side__label">{t('web.home.sidebar.yourChannels')}</div>
        {channelsForSidebar.map((c) => (
          <button
            key={c.id}
            type="button"
            className="nw-side__item"
            onClick={() => navigate(`/channel/${encodeURIComponent(c.id)}`)}
          >
            <ChannelLogo name={c.name} logoUrl={c.logoUrl} size={22} />
            <span className="nw-side__item-label">{c.name}</span>
          </button>
        ))}
        <div className="nw-side__divider" />
        <button type="button" className="nw-side__item" onClick={() => navigate('/explore')}>
          <span className="nw-side__item-label">{t('web.home.sidebar.discoverMore')}</span>
        </button>
      </aside>

      <section className="nw-feed">
        <div className="nw-feed__head">
          <div>
            <h1 className="nw-feed__title">
              {t(pickGreetingKey(), { name: firstName(greetingName) })}
            </h1>
            <p className="nw-feed__sub">
              {articles.length > 0
                ? t('web.home.feedSummary', { count: articles.length, language: lang.name })
                : t('web.home.feedSummaryEmpty', { language: lang.name })}
            </p>
          </div>
        </div>

        <AlertsSection alerts={alerts} onOpenAlert={setOpenAlert} />

        {/* Scope chips (All / My channels) flow inline with the topic
            chips. localStorage persists the choice across reloads. */}
        <div
          className="nw-feed__topics"
          role="tablist"
          aria-label={t('web.home.scope.ariaLabel')}
        >
          <button
            type="button"
            role="tab"
            aria-selected={!subscribedOnly}
            className={`nw-topic-chip ${!subscribedOnly ? 'is-on' : ''}`}
            onClick={() => setSubscribedOnly(false)}
          >
            {t('web.home.scope.all')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subscribedOnly}
            className={`nw-topic-chip ${subscribedOnly ? 'is-on' : ''}`}
            onClick={() => setSubscribedOnly(true)}
          >
            {t('web.home.scope.myChannels')}
          </button>
          <span className="nw-feed__topics-divider" aria-hidden />
          {FEED_TOPICS.map((t) => (
            <button
              key={t}
              type="button"
              className={`nw-topic-chip ${topic === t ? 'is-on' : ''}`}
              onClick={() => setTopic(t)}
            >
              {t}
            </button>
          ))}
        </div>

        {feedQuery.isLoading && (
          <div className="nw-feed__loading">
            <div className="nw-feed__loading-spinner" aria-hidden />
            {t('web.home.loading')}
          </div>
        )}
        {feedQuery.isError && !feedQuery.isLoading && (
          <div className="nw-feed__empty">{t('web.home.errorNetwork')}</div>
        )}
        {!feedQuery.isLoading &&
          !feedQuery.isError &&
          articles.length === 0 &&
          (subscribedOnly ? (
            <div className="nw-feed__empty">
              <div>{t('web.home.emptySubscribed')}</div>
              <button
                type="button"
                className="nw-btn nw-btn--primary"
                style={{ marginTop: 12 }}
                onClick={() => navigate('/explore')}
              >
                {t('web.home.emptySubscribedCta')}
              </button>
            </div>
          ) : (
            <div className="nw-feed__empty">{t('web.home.emptyTopic')}</div>
          ))}

        {articles.length > 0 && (
          <div className="nw-feed__grid">
            {articles.map((a) => (
              <ArticleCard key={a.id} article={a} isSaved={savedSet.has(a.id)} />
            ))}
          </div>
        )}

        {feedQuery.hasNextPage && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              type="button"
              className="nw-btn nw-btn--ghost"
              onClick={() => feedQuery.fetchNextPage()}
              disabled={feedQuery.isFetchingNextPage}
            >
              {feedQuery.isFetchingNextPage ? t('web.home.loadingMore') : t('web.home.loadMore')}
            </button>
          </div>
        )}
      </section>

      <aside className="nw-aside">
        <div className="nw-card-block">
          <h3 className="nw-card-block__title">{t('web.home.trending.title')}</h3>
          <div className="nw-trending">
            {trending.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {t('web.home.trending.empty')}
              </div>
            )}
            {trending.map((a, i) => (
              <button
                key={a.id}
                type="button"
                className="nw-trending__row"
                onClick={() => navigate(`/article/${encodeURIComponent(a.id)}`)}
              >
                <div className="nw-trending__num">{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nw-trending__title">{a.title}</div>
                  <div className="nw-trending__meta">
                    {a.channelName}
                    {a.publishedAt ? ` · ${shortDate(a.publishedAt)}` : ''}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

      </aside>

      <AlertDetailModal alert={openAlert} onClose={() => setOpenAlert(null)} />
    </div>
  );
}

// Pick a full-sentence greeting KEY based on local hour rather than
// templating a noun ("morning") into a parent sentence ("Good {{noun}},
// {{name}}."). Templating the noun locks every locale into the
// English-shaped subject-noun-comma-name grammar, which fails for
// Hindi (SOV order), Arabic (vocative form depends on time of day),
// Finnish (case declension on the time-of-day word), and Thai
// (politeness particle position). Three full-sentence keys give every
// translator the freedom to phrase the entire greeting natively.
function pickGreetingKey():
  | 'web.home.greeting.morning'
  | 'web.home.greeting.afternoon'
  | 'web.home.greeting.evening' {
  const h = new Date().getHours();
  if (h < 12) return 'web.home.greeting.morning';
  if (h < 18) return 'web.home.greeting.afternoon';
  return 'web.home.greeting.evening';
}

function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
